import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";

import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PauseCircleOutlineRoundedIcon from "@mui/icons-material/PauseCircleOutlineRounded";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";

import { Box, MenuItem, TextField, Typography } from "@mui/material";

import ChoiceGroup from "../../../../components/Administration/Organization/OrganizationDetails/ChoiceGroup";
import OrganizationTemplate from "../../../../components/Administration/Organization/OrganizationDetails/OrganizationTemplate";

import themeCollection from "../../../../theme/themeCollection";

const VisibilityType = Object.freeze({
  PRIVATE: "PRIVATE",
  PUBLIC: "PUBLIC",
});

const OrganizationStatus = Object.freeze({
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
});

const ui = {
  pageBg: "#f7f8fa",
  panel: "#ffffff",
  railBg: "#ffffff",
  border: "#e6e9ee",
  borderStrong: "#d9dee7",
  text: "#182230",
  muted: "#667085",
  subtle: "#98a2b3",
  soft: "#f8fafc",
  successBg: "#eefaf2",
  successBorder: "#b7e4c4",
  successText: "#17733b",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    height: { xs: 40, sm: 36 },
    borderRadius: 0.75,
    bgcolor: "#fff",
    fontSize: 12.8,
    "& fieldset": { borderColor: ui.borderStrong },
    "&:hover fieldset": { borderColor: "#c5ccd6" },
    "&.Mui-focused fieldset": { borderWidth: 1, borderColor: "primary.main" },
    "&.Mui-focused": {
      boxShadow: theme => `0 0 0 3px ${theme.palette.primary.main}12`,
    },
  },
  "& .MuiOutlinedInput-input": {
    height: { xs: 40, sm: 36 },
    boxSizing: "border-box",
    px: 1.15,
    py: 0,
  },
  "& .MuiSelect-select": {
    minHeight: { xs: "40px !important", sm: "36px !important" },
    display: "flex",
    alignItems: "center",
    py: "0 !important",
  },
};

const labelSx = {
  display: "block",
  mb: 0.45,
  color: ui.muted,
  fontSize: 11.2,
  lineHeight: 1.25,
  fontWeight: 650,
};

const OrganizationConfiguration = ({
  companyId,
  disabled,
  draft,
  onChange,
  onImported,
}) => {
  const intl = useIntl();

  const visibilityDescription =
    draft.visibility === VisibilityType.PRIVATE
      ? intl.formatMessage({ id: "admin.organizationDetails.visibility.privateDescription" })
      : intl.formatMessage({ id: "admin.organizationDetails.visibility.publicDescription" });

  const renderThemeSelect = () => (
    <TextField
      select
      fullWidth
      size="small"
      disabled={disabled || !companyId}
      value={draft.colorTheme}
      onChange={event => onChange("colorTheme", event.target.value)}
      sx={inputSx}
      SelectProps={{
        displayEmpty: true,
        renderValue: value => {
          if (!value) {
            return (
              <Typography component="span" sx={{ color: ui.subtle, fontSize: 12 }}>
                {intl.formatMessage({ id: "common.notSet" })}
              </Typography>
            );
          }

          const option = themeCollection.find(item => item.value === value);
          return option?.label ?? value;
        },
      }}
    >
      {themeCollection.map(option => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <Box
      sx={{
        px: { xs: 1.35, sm: 1.6 },
        pt: 0.35,
        pb: { xs: 1.35, sm: 1.55 },
        flexGrow: 1,
      }}
    >
      <Box
        sx={{
          display: { xs: "block", sm: "grid", lg: "block" },
          gridTemplateColumns: { sm: "repeat(2, minmax(0, 1fr))" },
          columnGap: { sm: 2 },
          alignItems: "start",
        }}
      >
        <Box>
          <Typography sx={{ ...labelSx, mt: 1.35 }}>
            <FormattedMessage id="common.status" />
          </Typography>
          <ChoiceGroup
            value={draft.active ? OrganizationStatus.ACTIVE : OrganizationStatus.INACTIVE}
            onChange={value => onChange("active", value === OrganizationStatus.ACTIVE)}
            disabled={disabled}
            options={[
              {
                value: OrganizationStatus.ACTIVE,
                label: intl.formatMessage({ id: "admin.organizationDetails.status.active" }),
                icon: <CheckCircleRoundedIcon />,
                tone: "success",
              },
              {
                value: OrganizationStatus.INACTIVE,
                label: intl.formatMessage({ id: "admin.organizationDetails.status.inactive" }),
                icon: <PauseCircleOutlineRoundedIcon />,
                tone: "default",
              },
            ]}
          />

          <Typography sx={{ ...labelSx, mt: 1.35 }}>
            <FormattedMessage id="admin.companySettings.basicSettings.visibilitySetting.Title" />
          </Typography>
          <ChoiceGroup
            value={draft.visibility}
            onChange={value => onChange("visibility", value)}
            disabled={disabled}
            options={[
              {
                value: VisibilityType.PUBLIC,
                label: intl.formatMessage({ id: "admin.organizationDetails.visibility.publicShort" }),
                icon: <PublicOutlinedIcon />,
                tone: "success",
              },
              {
                value: VisibilityType.PRIVATE,
                label: intl.formatMessage({ id: "admin.organizationDetails.visibility.privateShort" }),
                icon: <LockOutlinedIcon />,
                tone: "default",
              },
            ]}
          />
          <Typography sx={{ mt: 0.5, color: ui.muted, fontSize: 10.7, lineHeight: 1.4 }}>
            {visibilityDescription}
          </Typography>
        </Box>

        <Box sx={{ mt: { xs: 1.55, sm: 0, lg: 1.55 } }}>
          <Box>
            <Typography sx={labelSx}>
              <FormattedMessage id="admin.companySettings.colorTheme.Title" />
            </Typography>
            {renderThemeSelect()}
            <Typography sx={{ mt: 0.45, color: ui.muted, fontSize: 10.55, lineHeight: 1.35 }}>
              <FormattedMessage id="admin.companySettings.colorTheme.warning" />
            </Typography>
          </Box>

          <OrganizationTemplate
            companyId={companyId}
            disabled={disabled}
            onImported={onImported}
          />
        </Box>
      </Box>
    </Box>
  );
};

OrganizationConfiguration.propTypes = {
  companyId: PropTypes.number,
  disabled: PropTypes.bool.isRequired,
  draft: PropTypes.shape({
    active: PropTypes.bool,
    colorTheme: PropTypes.string,
    contactPerson: PropTypes.string,
    email: PropTypes.string,
    name: PropTypes.string,
    organizationNumber: PropTypes.string,
    reference: PropTypes.string,
    telephone: PropTypes.string,
    vatNumber: PropTypes.string,
    visibility: PropTypes.oneOf(Object.values(VisibilityType)),
    website: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onImported: PropTypes.func.isRequired,
};

OrganizationConfiguration.defaultProps = {
  companyId: null,
  disabled: false,
};

OrganizationConfiguration.displayName = "OrganizationConfiguration";

export default OrganizationConfiguration;
